import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Database, FileArchive, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLocation } from 'wouter';

export default function DataExport() {
  const [, navigate] = useLocation();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const { data: stats, isLoading: statsLoading } = trpc.system.exportStats.useQuery();

  const handleExport = async () => {
    setIsExporting(true);
    setExportStatus('idle');
    setErrorMessage('');

    try {
      // Trigger download via direct link
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const link = document.createElement('a');
      link.href = '/api/export/download';
      link.download = `water-well-export-${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportStatus('success');
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate('/') }>
          ← Back to Dashboard
        </Button>
        <h1 className="text-3xl font-bold mb-2">Data Export & Backup</h1>
        <p className="text-muted-foreground">
          Export all system data for backup, archival, or migration purposes
        </p>
      </div>

      {/* Export Statistics */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Statistics
          </CardTitle>
          <CardDescription>
            Current data volume across all tables
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading statistics...
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Wells" value={stats.wells} />
              <StatCard label="Users" value={stats.users} />
              <StatCard label="Daily Logs" value={stats.dailyLogs} />
              <StatCard label="Chemical Logs" value={stats.chemicalLogs} />
              <StatCard label="Attachments" value={stats.logAttachments} />
              <StatCard label="Custom Fields" value={stats.customFields} />
              <StatCard label="Import History" value={stats.importHistory} />
              <StatCard 
                label="Total Records" 
                value={stats.totalRecords} 
                highlight 
              />
            </div>
          ) : (
            <p className="text-muted-foreground">No data available</p>
          )}
        </CardContent>
      </Card>

      {/* Export Action */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5" />
            Full Data Export
          </CardTitle>
          <CardDescription>
            Download a complete backup of all system data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm">Export Contents</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>CSV files:</strong> wells.csv, users.csv, dailyLogs.csv, chemicalLogs.csv, logAttachments.csv, customFields.csv, importHistory.csv</li>
              <li>• <strong>JSON files:</strong> wells.json, users.json, dailyLogs.json, chemicalLogs.json, logAttachments.json, customFields.json, importHistory.json</li>
              <li>• <strong>Metadata:</strong> export_metadata.json (timestamp, version, record counts)</li>
              <li>• <strong>Format:</strong> ZIP archive with maximum compression</li>
              <li>• <strong>Security:</strong> User passwords are excluded from export</li>
            </ul>
          </div>

          {exportStatus === 'success' && (
            <Alert className="border-green-500/50 bg-green-500/10">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-600">
                Export started successfully. Your download should begin shortly.
              </AlertDescription>
            </Alert>
          )}

          {exportStatus === 'error' && (
            <Alert className="border-red-500/50 bg-red-500/10">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-600">
                Export failed: {errorMessage || 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={handleExport} 
            disabled={isExporting || statsLoading}
            size="lg"
            className="w-full"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Export...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download Full Export (.zip)
              </>
            )}
          </Button>

          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>Use Cases:</strong></p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>Regular backups before major changes</li>
              <li>Data migration to another system</li>
              <li>Compliance and audit requirements</li>
              <li>Disaster recovery planning</li>
              <li>Historical archival</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Backup Best Practices</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>Schedule regular exports:</strong> Weekly or monthly depending on data change frequency</p>
          <p>• <strong>Store securely:</strong> Keep backups in a secure, off-site location</p>
          <p>• <strong>Test restoration:</strong> Periodically verify that exports can be imported successfully</p>
          <p>• <strong>Version control:</strong> Keep multiple backup versions (e.g., last 3 months)</p>
          <p>• <strong>Document process:</strong> Maintain documentation for backup and restore procedures</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, highlight = false }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-primary/5 border-primary/20' : 'bg-card'}`}>
      <div className="text-2xl font-bold">{value.toLocaleString()}</div>
      <div className={`text-xs ${highlight ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
        {label}
      </div>
    </div>
  );
}
